import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';
import { confirm } from '../components/ui/confirm-dialog';
import { useAuth } from '../context/AuthContext';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import organisationService from '../services/organisationService';
import {
  Network, Plus, Pencil, Trash2, HeartHandshake, Layers, ExternalLink,
} from 'lucide-react';

/*
 * Organisation Setup — the commercial layer over the company:
 *
 *   Company (Restaurant) → Restaurant Group → Location (branch)
 *
 * Groups are CREATED and mapped to branches on Settings → Restaurant Groups
 * (the other half of this pair); here they are read-only context. What this
 * screen owns is the FRANCHISE ARRANGEMENT register: under what agreement a
 * slice of the company — the whole company, one group, or one branch — is
 * operated. Records, not billing: nothing here changes what a guest pays.
 *
 * Deliberately absent: hours, floor plans and location editing.
 */

// Labels come from t(`org.type.${value}`) — the VALUE is server data, never translated.
const ARRANGEMENT_TYPES = [
  { value: 'COMPANY_OWNED', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'FRANCHISED', cls: 'bg-amber-100 text-amber-700' },
  { value: 'JOINT_VENTURE', cls: 'bg-sky-100 text-sky-700' },
  { value: 'MANAGED', cls: 'bg-violet-100 text-violet-700' },
];
const typeMeta = (v) => ARRANGEMENT_TYPES.find((x) => x.value === v) || ARRANGEMENT_TYPES[1];

const selectCls = 'w-full rounded-md border px-3 py-2 text-sm bg-background';

// One select drives the scope: '' = whole company, 'g:<id>' = a group,
// 'l:<id>' = a branch. Decomposed into group_id/location_id on save.
const scopeValue = (f) => (f.location_id ? `l:${f.location_id}` : f.group_id ? `g:${f.group_id}` : '');
const scopeToIds = (v) => ({
  group_id: v.startsWith('g:') ? Number(v.slice(2)) : null,
  location_id: v.startsWith('l:') ? Number(v.slice(2)) : null,
});

const Toggle = ({ checked, onChange }) => (
  <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-emerald-500' : 'bg-gray-300'}`}>
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
      checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const Label = ({ children }) => (
  <label className="text-[11px] text-gray-500">{children}</label>
);

export default function OrganisationSetup() {
  const { t } = useT();
  const { toast } = useToast();
  const { user } = useAuth();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = user?.restaurant_id || ctxRestaurantId;

  const [data, setData] = useState(null);
  const [franchiseEdit, setFranchiseEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!restaurantId) return;
    organisationService.overview(restaurantId)
      .then(setData)
      .catch((err) => toast({ title: 'Error', description: err.message, variant: 'destructive' }));
  }, [restaurantId, toast]);

  useEffect(() => { load(); }, [load]);

  const oops = (err) => toast({ title: 'Error', description: err.message, variant: 'destructive' });

  const saveFranchise = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const body = { ...franchiseEdit, ...scopeToIds(franchiseEdit.scope || '') };
      delete body.scope;
      if (franchiseEdit.id) {
        await organisationService.saveFranchise(restaurantId, franchiseEdit.id, body);
      } else {
        await organisationService.createFranchise(restaurantId, body);
      }
      setFranchiseEdit(null);
      load();
    } catch (err) { oops(err); } finally { setBusy(false); }
  };

  const dropFranchise = async (f) => {
    if (!(await confirm({
      title: t('org.fr.deleteTitle'),
      description: t('org.fr.deleteDesc'),
      confirmText: t('org.common.delete'), tone: 'destructive',
    }))) return;
    try { await organisationService.deleteFranchise(restaurantId, f.id); load(); }
    catch (err) { oops(err); }
  };

  const fe = (k, v) => setFranchiseEdit((prev) => ({ ...prev, [k]: v }));

  if (!restaurantId) return <div className="p-6 text-gray-500">{t('org.noRestaurant')}</div>;
  if (!data) return <div className="p-6 text-gray-500">{t('org.loading')}</div>;

  const ungrouped = data.locations.filter((l) => !l.group_id).length;

  const coversCell = (f) => {
    if (f.location_name) return { kind: t('org.scope.branch'), name: f.location_name };
    if (f.group_name) return { kind: t('org.scope.group'), name: f.group_name };
    return { kind: '', name: t('org.scope.company') };
  };

  return (
    <div className="p-6 w-full">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
            <Network className="h-7 w-7 text-amber-600" /> {t('org.title')}
          </h1>
          <p className="mt-1.5 text-sm text-gray-500">{t('org.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        {/* Restaurant groups — read-only context; managed on their own screen */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Layers size={16} className="text-amber-600" /> {t('org.groups.title')}
            </h3>
            <Button size="sm" variant="outline" asChild>
              <Link to="/settings/restaurant-groups">
                <ExternalLink size={14} className="mr-1" /> {t('org.groups.manage')}
              </Link>
            </Button>
          </div>
          <p className="text-xs text-gray-500 mb-3">{t('org.groups.hint')}</p>
          {data.groups.length === 0 ? (
            <p className="text-sm text-gray-500">{t('org.groups.none')}</p>
          ) : (
            <div className="space-y-1.5">
              {data.groups.map((g) => (
                <div key={g.id}
                  className={`flex items-center justify-between gap-2 rounded-lg border p-2.5 ${g.is_active ? '' : 'opacity-50'}`}>
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">{g.name}</span>
                    {g.code && (
                      <span className="ml-2 rounded bg-accent px-1.5 py-0.5 text-[11px] font-medium text-gray-600">{g.code}</span>
                    )}
                    {g.contact_name && (
                      <div className="text-xs text-gray-500 truncate">{g.contact_name}</div>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-gray-500">
                    {t('org.groups.branches', { n: g.location_count })}
                  </span>
                </div>
              ))}
            </div>
          )}
          {ungrouped > 0 && (
            <p className="mt-3 text-xs text-gray-500">{t('org.groups.ungrouped', { n: ungrouped })}</p>
          )}
        </Card>

        {/* Franchise arrangements — the register this screen owns */}
        <Card className="p-5 xl:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <HeartHandshake size={16} className="text-amber-600" /> {t('org.fr.title')}
            </h3>
            <Button size="sm" variant="outline"
              onClick={() => setFranchiseEdit({
                scope: '',
                arrangement_type: 'FRANCHISED',
                franchisee_company: '', franchisee_name: '', franchisee_brn: '',
                franchisee_phone: '', franchisee_email: '',
                royalty_percent: '0', marketing_levy_percent: '0', monthly_fee: '0',
                agreement_start: '', agreement_end: '', territory: '', notes: '',
                is_active: true,
              })}>
              <Plus size={14} className="mr-1" /> {t('org.fr.add')}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mb-3">{t('org.fr.hint')}</p>
          {data.franchises.length === 0 ? (
            <p className="text-sm text-gray-500">{t('org.fr.none')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-3 font-medium">{t('org.fr.covers')}</th>
                    <th className="py-2 pr-3 font-medium">{t('org.fr.arrangement')}</th>
                    <th className="py-2 pr-3 font-medium">{t('org.fr.franchisee')}</th>
                    <th className="py-2 pr-3 font-medium text-right">{t('org.fr.royalty')}</th>
                    <th className="py-2 pr-3 font-medium text-right">{t('org.fr.marketing')}</th>
                    <th className="py-2 pr-3 font-medium text-right">{t('org.fr.monthlyFee')}</th>
                    <th className="py-2 pr-3 font-medium">{t('org.fr.term')}</th>
                    <th className="py-2 pr-3 font-medium">{t('org.fr.status')}</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {data.franchises.map((f) => {
                    const meta = typeMeta(f.arrangement_type);
                    const covers = coversCell(f);
                    return (
                      <tr key={f.id} className={`border-b last:border-0 ${f.is_active ? '' : 'opacity-50'}`}>
                        <td className="py-2 pr-3">
                          <span className="font-medium text-gray-900">{covers.name}</span>
                          {covers.kind && (
                            <div className="text-xs text-gray-500">{covers.kind}</div>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
                            {t(`org.type.${meta.value}`)}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="text-gray-900">{f.franchisee_company || f.franchisee_name || '—'}</span>
                          {f.franchisee_company && f.franchisee_name && (
                            <div className="text-xs text-gray-500">{f.franchisee_name}</div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{Number(f.royalty_percent)}%</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{Number(f.marketing_levy_percent)}%</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{Number(f.monthly_fee).toLocaleString()}</td>
                        <td className="py-2 pr-3 text-xs text-gray-500 whitespace-nowrap">
                          {f.agreement_start || '…'} → {f.agreement_end || t('org.fr.open')}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          {f.is_active
                            ? <span className="text-emerald-700">{t('org.fr.active')}</span>
                            : <span className="text-gray-500">{t('org.fr.ended')}</span>}
                        </td>
                        <td className="py-2 text-right whitespace-nowrap">
                          <button type="button" onClick={() => setFranchiseEdit({
                            ...f,
                            scope: scopeValue(f),
                            agreement_start: f.agreement_start || '',
                            agreement_end: f.agreement_end || '',
                          })}
                            className="p-1 rounded hover:bg-accent text-gray-600"><Pencil size={14} /></button>
                          <button type="button" onClick={() => dropFranchise(f)}
                            className="p-1 rounded hover:bg-accent text-red-600 ml-1"><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Franchise form */}
      {franchiseEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setFranchiseEdit(null)}>
          <Card className="w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {franchiseEdit.id ? t('org.fr.editTitle') : t('org.fr.add')}
            </h2>
            <form onSubmit={saveFranchise} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>{t('org.fr.covers')}</Label>
                  <select className={selectCls} value={franchiseEdit.scope || ''}
                    onChange={(e) => fe('scope', e.target.value)}>
                    <option value="">{t('org.scope.company')}</option>
                    {data.groups.length > 0 && (
                      <optgroup label={t('org.scope.groups')}>
                        {data.groups.map((g) => (
                          <option key={g.id} value={`g:${g.id}`}>{g.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {data.locations.length > 0 && (
                      <optgroup label={t('org.scope.branches')}>
                        {data.locations.map((l) => (
                          <option key={l.id} value={`l:${l.id}`}>{l.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <div>
                  <Label>{t('org.fr.arrangement')}</Label>
                  <select className={selectCls} value={franchiseEdit.arrangement_type}
                    onChange={(e) => fe('arrangement_type', e.target.value)}>
                    {ARRANGEMENT_TYPES.map((x) => (
                      <option key={x.value} value={x.value}>{t(`org.type.${x.value}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>{t('org.form.company')}</Label>
                  <Input value={franchiseEdit.franchisee_company}
                    onChange={(e) => fe('franchisee_company', e.target.value)} />
                </div>
                <div>
                  <Label>{t('org.form.contact')}</Label>
                  <Input value={franchiseEdit.franchisee_name}
                    onChange={(e) => fe('franchisee_name', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>{t('org.form.brn')}</Label>
                  <Input value={franchiseEdit.franchisee_brn}
                    onChange={(e) => fe('franchisee_brn', e.target.value)} />
                </div>
                <div>
                  <Label>{t('org.form.phone')}</Label>
                  <Input value={franchiseEdit.franchisee_phone}
                    onChange={(e) => fe('franchisee_phone', e.target.value)} />
                </div>
                <div>
                  <Label>{t('org.form.email')}</Label>
                  <Input type="email" value={franchiseEdit.franchisee_email}
                    onChange={(e) => fe('franchisee_email', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>{t('org.form.royaltyPct')}</Label>
                  <Input type="number" step="0.01" min="0" max="100" value={franchiseEdit.royalty_percent}
                    onChange={(e) => fe('royalty_percent', e.target.value)} />
                </div>
                <div>
                  <Label>{t('org.form.marketingPct')}</Label>
                  <Input type="number" step="0.01" min="0" max="100" value={franchiseEdit.marketing_levy_percent}
                    onChange={(e) => fe('marketing_levy_percent', e.target.value)} />
                </div>
                <div>
                  <Label>{t('org.fr.monthlyFee')}</Label>
                  <Input type="number" step="0.01" min="0" value={franchiseEdit.monthly_fee}
                    onChange={(e) => fe('monthly_fee', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>{t('org.form.from')}</Label>
                  <Input type="date" value={franchiseEdit.agreement_start}
                    onChange={(e) => fe('agreement_start', e.target.value)} />
                </div>
                <div>
                  <Label>{t('org.form.until')}</Label>
                  <Input type="date" value={franchiseEdit.agreement_end}
                    onChange={(e) => fe('agreement_end', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>{t('org.form.territory')}</Label>
                <Input value={franchiseEdit.territory}
                  onChange={(e) => fe('territory', e.target.value)} />
              </div>
              <div>
                <Label>{t('org.form.notes')}</Label>
                <Input value={franchiseEdit.notes}
                  onChange={(e) => fe('notes', e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <Label>{t('org.form.active')}</Label>
                <Toggle checked={!!franchiseEdit.is_active} onChange={(v) => fe('is_active', v)} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setFranchiseEdit(null)}>
                  {t('org.common.cancel')}
                </Button>
                <Button type="submit" disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white">
                  {t('org.common.save')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
